import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SiteMember } from '../../../api/sitesV2Api';
import { RoleBadges } from '../../../shared/ui/RoleBadges';
import './AddParticipantModal.css';
import { AxiosError } from 'axios';

interface AddParticipantModalProps {
  isOpen: boolean;
  siteId: number;
  questionId: number;
  availableMembers: SiteMember[];
  onClose: () => void;
  onAddParticipant: (userId: number) => Promise<void>;
  addableRoleIds: number[] | null;
}

const AddParticipantModal: React.FC<AddParticipantModalProps> = ({
  isOpen,
  siteId,
  questionId,
  availableMembers,
  onClose,
  onAddParticipant,
  addableRoleIds
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [addingParticipant, setAddingParticipant] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Генерируем цвет аватара на основе ID пользователя
  const getAvatarColor = (userId: number) => {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
    ];
    return colors[userId % colors.length];
  };

  // Получаем инициалы пользователя
  const getUserInitials = (userName: string) => {
    return userName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Фильтруем участников по поисковому запросу
  const filteredMembers = availableMembers.filter(member => {
    const searchLower = searchTerm.toLowerCase();
    const matchesName = member.userName?.toLowerCase().includes(searchLower);
    const matchesOldRole = member.roleName?.toLowerCase().includes(searchLower);
    const matchesNewRoles = member.roles?.some(r => 
      r.isActive && r.roleName?.toLowerCase().includes(searchLower)
    );
    return matchesName || matchesOldRole || matchesNewRoles;
  });

  const handleAddParticipant = async (userId: number) => {
    try {
      setErrorMessage(null); // Сбрасываем предыдущую ошибку
      setAddingParticipant(userId);
      await onAddParticipant(userId);
      onClose(); // Закрываем модалку после успешного добавления
    } catch (error) {
      console.error('Error adding participant:', error);
      if (error instanceof AxiosError && error.response) {
        // Устанавливаем сообщение об ошибке из ответа сервера
        setErrorMessage(error.response.data.error || 'Произошла неизвестная ошибка');
      } else {
        setErrorMessage('Произошла неизвестная ошибка');
      }
    } finally {
      setAddingParticipant(null);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  // Сбрасываем ошибку при закрытии/открытии модалки
  useEffect(() => {
    if (!isOpen) {
      setErrorMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="add-participant-modal-overlay" onClick={onClose}>
      <div className="add-participant-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Добавить участника</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="modal-content">
          {errorMessage && (
            <div className="error-message">
              <i className="ri-error-warning-line"></i>
              {errorMessage}
            </div>
          )}

          <div className="search-section">
            <div className="search-input-wrapper">
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="Поиск по имени или роли..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="members-list">
            {filteredMembers.length === 0 ? (
              <div className="empty-state">
                <i className="ri-user-line"></i>
                <p>
                  {searchTerm ? 'Участники не найдены' : 'Нет доступных участников'}
                </p>
              </div>
            ) : (
              filteredMembers.map(member => {
                const hasNoRole = (member.roles?.length ?? 0) === 0 && (member.roleId == null);
                const canBeAdded = addableRoleIds === null
                  ? true
                  : hasNoRole
                    ? true
                    : (member.roles && member.roles.length > 0)
                      ? member.roles.some(r => r.isActive && addableRoleIds.includes(r.roleId))
                      : addableRoleIds.includes(member.roleId ?? -1);
                
                const isDisabled = addingParticipant === member.userId || !canBeAdded;

                return (
                  <div key={member.userId} className={`member-item ${!canBeAdded ? 'disabled' : ''}`}>
                    <div className="member-avatar">
                      <div 
                        className="avatar-circle"
                        style={{ background: getAvatarColor(member.userId) }}
                      >
                        {getUserInitials(member.userName || 'U')}
                      </div>
                    </div>
                    
                    <div className="member-info">
                      <div className="member-name">
                        {member.userName || 'Неизвестный пользователь'}
                      </div>
                      <div className="member-status">
                        <RoleBadges 
                          roles={member.roles} 
                          fallbackRoleName={member.roleName || 'Роль не назначена'}
                        />
                      </div>
                      {!canBeAdded && (
                        <div className="disabled-text">
                          Нет прав на добавление участников с этой ролью
                        </div>
                      )}
                    </div>
                    
                    <button
                      className="add-member-btn"
                      onClick={() => handleAddParticipant(member.userId)}
                      disabled={isDisabled}
                    >
                      {addingParticipant === member.userId ? (
                        <i className="ri-loader-4-line ri-spin"></i>
                      ) : (
                        <i className="ri-add-line"></i>
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddParticipantModal;
